import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid } from 'semantic-ui-react';
import { editResource, getAllEligibleTreaters, saveResource } from 'services/resourcesServices';
import styled from 'styled-components';
import { ResourceDto, UpsertResourceDto } from 'ts-types/api.types';
import { required } from 'utils/validatorUtils';


const UpsertResourcesFormContent = styled(FormContent)`
  max-width: 600px;
`;

interface Props extends MessageSourceProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  resourceId?: number,
  resource?: ResourceDto,
  resourceGroupOptions: DropdownOption[],
  locationOptions: DropdownOption[],
  mandateOptions: DropdownOption[],
  rapidTestCodeOptions: DropdownOption[]
}

interface State {
  treaterOptions: DropdownOption[],
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource,
}

class UpsertResourcesForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      treaterOptions: [],
      cancelTokenSource
    }

    this.initEligibleTreaters();
  }

  initEligibleTreaters = () => {

    const resourceId = this.props.resourceId
        ? this.props.resourceId
        : "available";

    getAllEligibleTreaters(resourceId, this.state.cancelTokenSource)
        .then(response => {

          const treaterOptions = response.map(treater => {
            return {
              key: treater.id,
              text: treater.name + ", " + treater.firstName,
              value: treater.id
            }
          });

          this.setState({
            treaterOptions
          })
        })
  };

  initialResourceValues = (): Partial<UpsertResourceDto> => {
    return {
      locationIds: [],
      mandateIds: [],
      active: true
    }
  }

  render(): React.ReactNode {

    const {resource = this.initialResourceValues()} = this.props;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={resource}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    )
  }

  handleError = (error: any) => {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [];

    const violations: Array<any> = error.violations;
    let knowErrorFound = false;
    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          knowErrorFound = true;
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    }

    if (!knowErrorFound) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  handleSubmit = async (values: Partial<UpsertResourceDto>) => {

    let saveValues: Partial<UpsertResourceDto> = {...values}

    const {resourceId, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    if (resourceId) {
      await editResource(resourceId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch((error) => this.handleError(error.response.data))
    } else {
      await saveResource(saveValues, cancelTokenSource)
          .then(onSave)
          .catch((error) => this.handleError(error.response.data))
    }
  };

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {
      onCancel,
      getMessage,
      resourceGroupOptions,
      locationOptions,
      mandateOptions,
      rapidTestCodeOptions
    } = this.props;
    const {treaterOptions, errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <UpsertResourcesFormContent>
              {
                  errorMessage && (
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }

              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={12}></Grid.Column>
                  <Grid.Column width={4} textAlign="right">
                    <Field
                        name="active"
                        component={CheckBox}
                        label={getMessage("resources.active")}
                        toggle={true}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("resources.description")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="description"
                        component={Input}
                        validate={required}
                        autoFocus
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("resources.resourceGroup")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        name="resourceGroupId"
                        component={Select}
                        options={resourceGroupOptions}
                        validate={required}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("resources.mandates")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        name="mandateIds"
                        component={Select}
                        validate={required}
                        options={mandateOptions}
                        searchable
                        multiple
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("resources.locations")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        name="locationIds"
                        component={Select}
                        validate={required}
                        options={locationOptions}
                        searchable
                        multiple
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("resources.treater")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        name="treaterId"
                        component={Select}
                        options={treaterOptions}
                        autoFocus
                        fluid
                        clearable
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("resources.rapidTestCode")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        name="rapidTestCode"
                        component={Select}
                        options={rapidTestCodeOptions}
                        clearable
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </UpsertResourcesFormContent>

            <FormButtonsRow>
              <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    )
  }
}

export default withMessages(UpsertResourcesForm);
